"use client";

import { motion } from "motion/react";
import {
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  ChevronRight,
  Loader2,
  ArrowRight,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { ISettings } from "@/slices/homeSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useRouter } from "next/navigation";
import { ErrorToast } from "../common/Toast";
import { emailSubscribe } from "@/services/home.services";
import React from "react";

export function Footer() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const RoomList = useAppSelector((state) => state.room.RoomList);
  const LandingPageList = useAppSelector(
    (state) => state.landingPage.LandingPageList,
  );
  const newsletterLoading = useAppSelector(
    (state) => state.home.newsletterLoading,
  );

  const Settings: Record<string, ISettings> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );

  const PhoneNo = Settings?.["PhoneNo"]?.SettingValue;
  const PhoneNo2 = Settings?.["PhoneNo2"]?.SettingValue;
  const PhoneNo3 = Settings?.["PhoneNo3"]?.SettingValue;

  const EmailID = Settings?.["EmailID"]?.SettingValue;
  const EmailID2 = Settings?.["EmailID2"]?.SettingValue;
  const EmailID3 = Settings?.["EmailID3"]?.SettingValue;

  const Address = Settings?.["Address"]?.SettingValue;
  const Address2 = Settings?.["Address2"]?.SettingValue;
  const Address3 = Settings?.["Address3"]?.SettingValue;

  const AddressMap = Settings?.["AddressMap"]?.SettingValue;
  const AddressMap2 = Settings?.["AddressMap2"]?.SettingValue;
  const AddressMap3 = Settings?.["AddressMap3"]?.SettingValue;

  const AddressIframe = Settings?.["AddressIframe"]?.SettingValue;

  const BusinessHours = Settings?.["BusinessHours"]?.SettingValue;
  const BusinessHours2 = Settings?.["BusinessHours2"]?.SettingValue;

  const FacebookLink = Settings?.["FacebookLink"]?.SettingValue;
  const InstaLink = Settings?.["InstaLink"]?.SettingValue;
  const XLink = Settings?.["XLink"]?.SettingValue;
  const LinkedInLink = Settings?.["LinkedInLink"]?.SettingValue;
  const YoutubeLink = Settings?.["YoutubeLink"]?.SettingValue;

  const CompanyName = Settings?.["CompanyName"]?.SettingValue;

  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Rooms & Suites", path: "/rooms" },
    { name: "Dining", path: "/dining" },
    // { name: "Events", path: "/events" },
    { name: "Gallery", path: "/gallery" },
    { name: "Blogs", path: "/blogs" },
    { name: "Attractions", path: "/attractions" },
    { name: "Offers", path: "/offers" },
    { name: "Book Direct", path: "/sarasiruham-direct-book" },
    { name: "Activities", path: "/activities" },
  ];

  const policies = [
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Terms & Conditions", path: "/terms-conditions" },
    // { name: "Cancellation Policy", path: "#" },
  ];

  const formik: any = useFormik({
    initialValues: {
      Email: "",
    },
    validateOnBlur: true,
    validationSchema: Yup.object({
      Email: Yup.string()
        .required("Email is required")
        .test("is-email-required", "Email is not valid", function (value) {
          if (!value) {
            return true;
          }
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          return emailRegex.test(value);
        }),
    }),
    onSubmit: async (
      values,
      {
        // setSubmitting,
        resetForm,
      },
    ) => {
      const payload = {
        EmailID: `${values?.Email}`,
      };
      const response = await dispatch(emailSubscribe(payload));
      if (response.meta.requestStatus === "fulfilled") {
        resetForm();
        router.push("/thank-you/");
      } else {
        ErrorToast("Error sending message!");
      }
    },
  });

  return (
    <>
      {LandingPageList &&
        LandingPageList.some((landingPage: any) => landingPage?.Type === 6) && (
          <div className="footer-links bg-[#221d19] border-t border-b border-white/10 py-3.5 sm:py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="content_section">
                <p
                  className="content_section_text text-xs sm:text-[13px] leading-[1.8] sm:leading-[1.9] text-white/70"
                  style={{ fontFamily: "Jost, sans-serif" }}
                >
                  {/* <strong className="text-[#d4af37] font-semibold text-[11px] sm:text-xs tracking-wider uppercase mr-2 inline-block">
                    Events :
                  </strong> */}
                  {LandingPageList?.filter(
                    (landingPage: any) => landingPage?.Type === 6,
                  )?.map((landingPage: any, index: number, arr: any[]) => {
                    const pageUrl = landingPage?.Url?.startsWith("/")
                      ? landingPage?.Url
                      : `/${landingPage?.Url || ""}`;
                    return (
                      <React.Fragment key={landingPage?.ID || index}>
                        <Link
                          href={pageUrl}
                          className="text-white/75 hover:text-[#d4af37] transition-colors inline hover:underline underline-offset-4 decoration-[#d4af37]/40"
                        >
                          {landingPage?.PageTitle}
                        </Link>
                        {arr?.length !== index + 1 && (
                          <span className="text-white/25 px-2 sm:px-2.5 select-none font-light">
                            |
                          </span>
                        )}
                      </React.Fragment>
                    );
                  })}
                </p>
              </div>
            </div>
          </div>
        )}
      <footer className="bg-gradient-to-br from-[#2a2520] via-[#3a3530] to-[#2a2520] text-white">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <Link href="/">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="relative h-16 mb-6 w-36"
                >
                  <Image
                    src="/imports/logo_2.png"
                    alt="Sarasiruham Resort"
                    fill
                    className="object-contain"
                  />
                </motion.div>
              </Link>
              <p
                className="text-white/70 mb-6 leading-relaxed"
                style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
              >
                Experience timeless luxury and serenity at Sarasiruham Resort,
                where every moment becomes a cherished memory.
              </p>

              {/* Social Icons */}
              <div className="flex gap-3">
                {[
                  { Icon: Facebook, link: FacebookLink, name: "Facebook" },
                  { Icon: Instagram, link: InstaLink, name: "Instagram" },
                  { Icon: Twitter, link: XLink, name: "Twitter" },
                  { Icon: Youtube, link: YoutubeLink, name: "Youtube" },
                ]
                  .filter((x) => x.link)
                  .map(({ Icon, link, name }, i) => (
                    <motion.a
                      key={i}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Visit our ${name} page`}
                      whileHover={{ scale: 1.1, backgroundColor: "#d4af37" }}
                      className="bg-white/10 p-2.5 backdrop-blur-sm transition-colors"
                    >
                      <Icon className="w-5 h-5" />
                    </motion.a>
                  ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3
                className="text-xl mb-6 text-[#d4af37]"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontWeight: 600,
                }}
              >
                Quick Links
              </h3>
              <ul className="space-y-3">
                {quickLinks.map((link, i) => (
                  <li key={i}>
                    <Link
                      href={link.path}
                      className="text-white/70 hover:text-[#d4af37] transition-colors flex items-center gap-2 group"
                      style={{
                        fontFamily: "Jost, sans-serif",
                        fontWeight: 300,
                      }}
                    >
                      <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -ml-6 group-hover:ml-0 transition-all" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Latest Blogs */}
            <div>
              <h3
                className="text-xl mb-6 text-[#d4af37]"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontWeight: 600,
                }}
              >
                Rooms
              </h3>
              <ul className="space-y-4">
                {RoomList?.slice(0, 8)?.map((roomCategory: any, i: number) => (
                  <li key={i}>
                    <Link
                      href={`/rooms/${roomCategory?.RoomURL}`}
                      className="group block"
                    >
                      <p
                        className="text-white/90 hover:text-[#d4af37] transition-colors mb-1 line-clamp-2"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 400,
                        }}
                      >
                        {roomCategory?.RoomTitle}
                      </p>
                      {/* <p
                      className="text-white/50 text-xs"
                      style={{
                        fontFamily: "Jost, sans-serif",
                        fontWeight: 300,
                      }}
                    >
                      {roomCategory?.date}
                    </p> */}
                    </Link>
                  </li>
                ))}
                {/* <li className="pt-2">
                <Link
                  href="/blog"
                  className="text-[#d4af37] hover:text-white transition-colors flex items-center gap-2 group"
                  style={{ fontFamily: "Jost, sans-serif", fontWeight: 500 }}
                >
                  View All Blogs
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </li> */}
              </ul>
            </div>

            {/* Contact Information */}
            <div>
              <h3
                className="text-xl mb-6 text-[#d4af37]"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontWeight: 600,
                }}
              >
                Get in Touch
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-1" />
                  <div>
                    {Address && (
                      <p
                        className="text-white/90 hover:text-[#d4af37] transition-colors block"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        <Link href={AddressMap || "#"} target="_blank">
                          {Address}
                        </Link>
                      </p>
                    )}
                    {Address2 && (
                      <p
                        className="text-white/90 hover:text-[#d4af37] transition-colors block mt-3"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        <Link href={AddressMap2 || "#"} target="_blank">
                          {Address2}
                        </Link>
                      </p>
                    )}
                    {Address3 && (
                      <p
                        className="text-white/90 hover:text-[#d4af37] transition-colors block mt-3"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        <Link href={AddressMap3 || "#"} target="_blank">
                          {Address3}
                        </Link>
                      </p>
                    )}
                    {/* <p
                    className="text-white/90"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
                  >
                    Lake Bagela, Nagda, Eklingji
                    <br />
                    Udaipur, Rajasthan, India
                  </p> */}
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-1" />
                  <div className="flex flex-col gap-1">
                    {PhoneNo && (
                      <Link
                        href={`tel:${PhoneNo}`}
                        className="text-white/90 hover:text-[#d4af37] transition-colors block"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        {PhoneNo}
                      </Link>
                    )}
                    {PhoneNo2 && (
                      <Link
                        href={`tel:${PhoneNo2}`}
                        className="text-white/90 hover:text-[#d4af37] transition-colors block"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        {PhoneNo2}
                      </Link>
                    )}
                    {PhoneNo3 && (
                      <Link
                        href={`tel:${PhoneNo3}`}
                        className="text-white/90 hover:text-[#d4af37] transition-colors block"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        {PhoneNo3}
                      </Link>
                    )}
                    {/* <a
                    href="tel:+919105700099"
                    className="text-white/90 hover:text-[#d4af37] transition-colors block"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
                  >
                    +91 9105700099
                  </a>
                  <a
                    href="tel:+917849902266"
                    className="text-white/90 hover:text-[#d4af37] transition-colors block"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
                  >
                    +91 7849902266
                  </a> */}
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-1" />
                  <div className="flex flex-col gap-1">
                    {EmailID && (
                      <Link
                        href={`mailto:${EmailID}`}
                        target="_blank"
                        className="text-white/90 hover:text-[#d4af37] transition-colors block break-all"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        {EmailID}
                      </Link>
                    )}
                    {EmailID2 && (
                      <Link
                        href={`mailto:${EmailID2}`}
                        target="_blank"
                        className="text-white/90 hover:text-[#d4af37] transition-colors block break-all"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        {EmailID2}
                      </Link>
                    )}
                    {EmailID3 && (
                      <Link
                        href={`mailto:${EmailID3}`}
                        target="_blank"
                        className="text-white/90 hover:text-[#d4af37] transition-colors block break-all"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        {EmailID3}
                      </Link>
                    )}
                    {/* <a
                    href="mailto:booking@sarasiruhamresort.com"
                    className="text-white/90 hover:text-[#d4af37] transition-colors block"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
                  >
                    booking@sarasiruhamresort.com
                  </a> */}
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Newsletter Signup */}
          <div className="border-t border-white/10 pt-12 mb-12">
            <div className="max-w-2xl mx-auto text-center">
              <h3
                className="text-2xl md:text-3xl mb-4 text-white"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontWeight: 600,
                }}
              >
                Subscribe to Our Newsletter
              </h3>
              <p
                className="text-white/70 mb-6"
                style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
              >
                Get exclusive offers, updates, and travel inspiration delivered
                to your inbox
              </p>
              <form
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                onSubmit={(e) => formik.handleSubmit(e)}
              >
                <input
                  name="Email" // ✅ REQUIRED
                  aria-label="Email Address"
                  type="email"
                  placeholder="Enter your email"
                  className={`flex-1 px-4 py-3 bg-white/10 border text-white placeholder-white/50 focus:outline-none focus:border-[#d4af37] transition-colors backdrop-blur-sm ${formik.errors.Email && formik.touched.Email
                      ? "border-red-500"
                      : "border-white/20"
                    }`}
                  style={{ fontFamily: "Jost, sans-serif" }}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values?.Email}
                />
                <motion.button
                  disabled={newsletterLoading}
                  type="submit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-[#d4af37] text-[#2a2520] px-8 py-3 hover:bg-[#c9a961] transition-colors shadow-lg flex cursor-pointer"
                  style={{ fontFamily: "Jost, sans-serif", fontWeight: 600 }}
                >
                  Subscribe
                  {newsletterLoading && (
                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                  )}
                </motion.button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p
                className="text-white/60 text-sm"
                style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
              >
                © {new Date().getFullYear()} Sarasiruham Resort. All rights
                reserved.
              </p>
              <div className="flex gap-6 text-sm justify-center md:justify-end">
                <Link
                  className="text-white/60 transition-colors"
                  href="https://aseuminfotech.com/"
                  target="_blank"
                >
                  Developed by{" "}
                  <span className="text-white hover:underline hover:text-[#d4af37] transition-colors">
                    ASEUM INFOTECH
                  </span>
                </Link>
              </div>
              <div className="flex gap-6">
                {policies?.map((policy, i) => (
                  <Link
                    key={i}
                    href={policy.path}
                    target="_blank"
                    className="text-white/60 hover:text-[#d4af37] text-sm transition-colors"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
                  >
                    {policy.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
